import React, { useState, useRef, useEffect } from "react";
import { normalizeCompany } from "../utils/companyUtils";
import { PREDEFINED_SUBTASKS } from "../utils/subtaskPresets";

// Interné statické šablóny pre rýchle vyplnenie formulára
const INTERNAL_PRESETS = [
  {
    id: "mesacne_uctovnictvo",
    text: "mesačné účtovníctvo",
    subtasks: "VFA (vyšlé faktúry)\nDFA (došlé faktúry)\nBanka – príjmy aj výdaje\nPokladňa – príjmy aj výdaje\nPohľadávky\nZáväzky\nID (interné doklady)\nPodanie DPH a KV k DPH\nPodanie DP k DPH\nVystavovanie faktúr",
    color: "#166534",
    bg: "#f0fdf4"
  },
  {
    id: "prihlasenie_zamestnanca",
    text: "prihlasenie zamestnanca",
    subtasks: "dotazník vyplnený\nprihlásenie SP\nprihlásenie ZP\npracovná zmluva\nvyhlásenie k dani\nvyhlásenie OOP socpoist",
    color: "#1976D2",
    bg: "transparent"
  }
];

// Počiatočný (prázdny) stav formulára — použitý pri inicializácii aj pri resete po odoslaní
const EMPTY_FORM_DATA = {
  formText: "",
  company: "",
  deadline: "",
  deadlineTime: "",
  formPeriod: "",
  repeatInterval: "none",
  estimatedTime: "",
  subtasksInput: "",
  formFiles: []
};

export default function PovinnostiForm({
  uniqueTaskTemplatesFiltered = [],
  showTaskSuggestions,
  setShowTaskSuggestions,
  povinnostInputRef,
  companyList = [],
  addToCompanyList,
  taskList = [],
  addToTaskList,
  handleAddTasks,
  // Nasledujúce props držíme pre spätnú kompatibilitu, ak by ich rodič explicitne vyžadoval
  setFormText,
  setAutocompleteText,
  setCompany,
  setSubtasksInput
}) {
  
  // Zjednotenie lokálneho stavu formulára (Koniec preťaženým props a lagovaniu UI)
  const [formData, setFormData] = useState(EMPTY_FORM_DATA);

  const [subtaskPickerOpen, setSubtaskPickerOpen] = useState(false);
  const [customSubtaskInput, setCustomSubtaskInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const subtaskPickerRef = useRef(null);
  const taskSuggestionsRef = useRef(null);

  // Spoločný štýl pre vstupné polia
  const commonInputStyle = {
    height: '36px',
    padding: '4px 10px',
    borderRadius: '6px',
    border: '1px solid #ddd',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
    background: 'white'
  };

  // Parsovanie podúloh
  const parsedLines = formData.subtasksInput ? formData.subtasksInput.split('\n').map(s => s.trim()).filter(Boolean) : [];
  const selectedPredefined = new Set(parsedLines.filter(l => PREDEFINED_SUBTASKS.includes(l)));
  const customItems = parsedLines.filter(l => !PREDEFINED_SUBTASKS.includes(l));

  // Bezpečné zatváranie dropdownov kliknutím mimo nich (Náhrada za problematický setTimeout)
  useEffect(() => {
    const handleOutsideClick = (e) => {
      // Zatvorenie našepkávača úloh
      if (taskSuggestionsRef.current && !taskSuggestionsRef.current.contains(e.target)) {
        setShowTaskSuggestions(false);
      }
      // Zatvorenie pickera podúloh
      if (subtaskPickerOpen && subtaskPickerRef.current && !subtaskPickerRef.current.contains(e.target)) {
        setSubtaskPickerOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [subtaskPickerOpen, setShowTaskSuggestions]);

  // Aktualizácia stavu jednotlivých polí
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Reset formulára do prázdneho stavu (napr. po úspešnom odoslaní)
  const resetForm = () => {
    setFormData(EMPTY_FORM_DATA);
    setCustomSubtaskInput('');
    // Synchronizácia legacy props naspäť na prázdne hodnoty, ak ich rodič využíva
    if (setFormText) setFormText('');
    if (setAutocompleteText) setAutocompleteText('');
    if (setCompany) setCompany('');
    if (setSubtasksInput) setSubtasksInput('');
  };

  // Správa podúloh
  const togglePredefined = (item) => {
    const newSelected = new Set(selectedPredefined);
    if (newSelected.has(item)) newSelected.delete(item);
    else newSelected.add(item);
    
    const newLines = [
      ...PREDEFINED_SUBTASKS.filter(p => newSelected.has(p)),
      ...customItems,
    ];
    handleInputChange('subtasksInput', newLines.join('\n'));
    if (setSubtasksInput) setSubtasksInput(newLines.join('\n'));
  };

  const addCustomItem = (text) => {
    if (!text.trim()) return;
    const newLines = [
      ...PREDEFINED_SUBTASKS.filter(p => selectedPredefined.has(p)),
      ...customItems,
      text.trim(),
    ];
    handleInputChange('subtasksInput', newLines.join('\n'));
    if (setSubtasksInput) setSubtasksInput(newLines.join('\n'));
    setCustomSubtaskInput('');
  };

  const removeCustomItem = (itemIndex) => {
    // Oprava bugu: Filtrujeme podľa indexu, nie podľa textu (ak by boli duplicity)
    const newCustomItems = customItems.filter((_, idx) => idx !== itemIndex);
    const newLines = [
      ...PREDEFINED_SUBTASKS.filter(p => selectedPredefined.has(p)),
      ...newCustomItems,
    ];
    handleInputChange('subtasksInput', newLines.join('\n'));
    if (setSubtasksInput) setSubtasksInput(newLines.join('\n'));
  };

  // Výber šablóny z ponuky
  const handleSelectTemplate = (text, subtasks) => {
    setFormData(prev => ({
      ...prev,
      formText: text,
      subtasksInput: subtasks || ""
    }));

    // Synchronizácia so starými callbackmi z rodiča (ak existujú)
    if (setFormText) setFormText(text);
    if (setAutocompleteText) setAutocompleteText(text);
    if (setSubtasksInput) setSubtasksInput(subtasks || "");
    if (addToTaskList && text && !taskList.includes(text)) addToTaskList(text);
    
    setShowTaskSuggestions(false);
  };

  const handleCompanyBlur = () => {
    if (formData.company) {
      const normalized = normalizeCompany(formData.company);
      handleInputChange('company', normalized);
      if (setCompany) setCompany(normalized);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    const normalized = normalizeCompany(formData.company);
    
    if (normalized && !companyList.includes(normalized)) {
      const ok = window.confirm(`Firma "${normalized}" nie je v zozname. Pridať ju?`);
      if (!ok) return;
      if (addToCompanyList) addToCompanyList(normalized);
    }
    
    if (normalized) {
      handleInputChange('company', normalized);
      if (setCompany) setCompany(normalized);
    }

    const taskNames = formData.formText.split(',').map(t => t.trim()).filter(Boolean);
    for (const name of taskNames) {
      if (name && !taskList.includes(name)) {
        const ok = window.confirm(`Povinnosť "${name}" nie je v zozname. Pridať ju?`);
        if (!ok) return;
        if (addToTaskList) addToTaskList(name);
      }
    }

    // Odovzdanie kompletných vyčistených dát do rodičovskej funkcie
    if (handleAddTasks) {
      setIsSubmitting(true);
      let result;
      try {
        result = await handleAddTasks({
          ...formData,
          company: normalized
        });
      } catch (error) {
        window.alert(`Úlohu sa nepodarilo uložiť: ${error.message || "Neznáma chyba"}`);
        return;
      } finally {
        setIsSubmitting(false);
      }
      if (result?.ok === false) {
        window.alert(`Úlohu sa nepodarilo uložiť: ${result.error || "Neznáma chyba"}`);
        return;
      }
    }

    // Vyčistenie formulára po úspešnom odoslaní
    resetForm();
  };

  return (
    <form onSubmit={handleSubmit} style={{ 
      maxWidth: "1350px", 
      margin: "0.5rem auto", 
      background: "rgba(255,255,255,0.1)", 
      borderRadius: 10, 
      padding: '10px 15px', 
      boxShadow: "0 2px 16px rgba(0,0,0,0.1)" 
    }}>
      <div style={{ 
        display: "flex", 
        gap: 10, 
        alignItems: "center", 
        flexWrap: "nowrap" 
      }}>
        
        {/* Názov povinnosti */}
        <div style={{ position: 'relative', flex: 3, minWidth: 200 }} ref={taskSuggestionsRef}>
          <input
            type="text"
            list="task-list-form"
            value={formData.formText}
            onChange={e => {
              handleInputChange('formText', e.target.value);
              if (setFormText) setFormText(e.target.value);
              if (setAutocompleteText) setAutocompleteText(e.target.value);
            }}
            placeholder="Povinnosť..."
            style={{ ...commonInputStyle, width: '100%' }}
            required
            ref={povinnostInputRef}
            autoComplete="off"
            onFocus={() => setShowTaskSuggestions(true)}
          />
          <datalist id="task-list-form">
            {taskList.map(t => <option key={t} value={t} />)}
          </datalist>
          
          {showTaskSuggestions && (
            <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                background: 'white',
                border: '1px solid #ccc',
                borderRadius: 6,
                zIndex: 100,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                fontSize: 14,
                maxHeight: 250,
                overflowY: 'auto',
                marginTop: '5px'
            }}>
              {/* Vstavané fixné šablóny */}
              {INTERNAL_PRESETS.map(preset => (
                <div
                  key={preset.id}
                  onClick={() => handleSelectTemplate(preset.text, preset.subtasks)}
                  style={{ 
                    padding: '8px 12px', 
                    cursor: 'pointer', 
                    color: preset.color, 
                    fontWeight: 'bold', 
                    background: preset.bg,
                    borderBottom: '1px solid #f0f0f0' 
                  }}
                >
                  ➕ {preset.text} (šablóna)
                </div>
              ))}
              
              {/* Dynamické šablóny z props */}
              {uniqueTaskTemplatesFiltered.map((tpl, idx) => (
                <div
                  key={`dynamic-${idx}`}
                  onClick={() => handleSelectTemplate(tpl.text, tpl.subtasks)}
                  style={{ padding: '8px 12px', borderBottom: '1px solid #f0f0f0', cursor: 'pointer' }}
                >
                  {tpl.text}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Firma */}
        <input
          type="text"
          list="company-list-form"
          value={formData.company}
          onChange={e => {
            const val = e.target.value.toUpperCase();
            handleInputChange('company', val);
            if (setCompany) setCompany(val);
          }}
          onBlur={handleCompanyBlur}
          placeholder="Firma..."
          style={{ ...commonInputStyle, flex: "1.5", minWidth: 130 }}
        />
        <datalist id="company-list-form">
          {companyList.map(c => <option key={c} value={c} />)}
        </datalist>

        {/* Termín */}
        <input
          type="date"
          value={formData.deadline}
          onChange={e => handleInputChange('deadline', e.target.value)}
          style={{ ...commonInputStyle, width: "135px" }}
          required
        />

        {/* Obdobie */}
        <input
          type="month"
          value={formData.formPeriod}
          onChange={e => handleInputChange('formPeriod', e.target.value)}
          title="Účtovné obdobie"
          style={{ ...commonInputStyle, width: "140px" }}
          required
        />

        {/* Čas termínu (voliteľný) */}
        <input
          type="time"
          value={formData.deadlineTime}
          onChange={e => handleInputChange('deadlineTime', e.target.value)}
          title="Čas termínu (voliteľné)"
          style={{ ...commonInputStyle, width: "100px" }}
        />

        {/* Interval */}
        <select
          value={formData.repeatInterval}
          onChange={e => handleInputChange('repeatInterval', e.target.value)}
          style={{ ...commonInputStyle, width: "110px" }}
        >
          <option value="none">Bez opakovania</option>
          <option value="weekly">Týždenne</option>
          <option value="monthly">Mesačne</option>
          <option value="quarterly">Štvrťročne</option>
          <option value="annually">Ročne</option>
        </select>

        {/* Odhadovaný čas */}
        <input
          type="number"
          value={formData.estimatedTime}
          onChange={e => handleInputChange('estimatedTime', e.target.value)}
          placeholder="Min"
          style={{ ...commonInputStyle, width: "65px" }}
          required
        />

        {/* Podúlohy - picker s checkboxmi */}
        <div style={{ position: 'relative', flex: '1.5', minWidth: 130 }} ref={subtaskPickerRef}>
          <button
            type="button"
            onClick={() => setSubtaskPickerOpen(v => !v)}
            style={{ 
              ...commonInputStyle, 
              width: '100%', 
              textAlign: 'left', 
              cursor: 'pointer', 
              background: parsedLines.length > 0 ? '#f0fdf4' : 'white', 
              border: parsedLines.length > 0 ? '1px solid #86efac' : '1px solid #ddd' 
            }}
          >
            {parsedLines.length > 0 ? `${parsedLines.length} podúloh ▾` : 'Podúlohy... ▾'}
          </button>
          
          {subtaskPickerOpen && (
            <div style={{ position: 'absolute', top: '100%', left: 0, minWidth: 280, background: 'white', border: '1px solid #ccc', borderRadius: 6, zIndex: 200, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', padding: '6px 0' }}>
              {PREDEFINED_SUBTASKS.map(item => (
                <label key={item} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 12px', cursor: 'pointer', fontSize: 13, background: selectedPredefined.has(item) ? '#f0fdf4' : 'transparent' }}>
                  <input type="checkbox" checked={selectedPredefined.has(item)} onChange={() => togglePredefined(item)} />
                  {item}
                </label>
              ))}
              
              {customItems.length > 0 && (
                <div style={{ borderTop: '1px solid #eee', marginTop: 4, paddingTop: 4 }}>
                  {customItems.map((item, i) => (
                    <div key={`custom-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 12px', fontSize: 13 }}>
                      <span style={{ flex: 1, color: '#334155' }}>· {item}</span>
                      <button type="button" onClick={() => removeCustomItem(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: 16, lineHeight: 1, padding: 0 }}>×</button>
                    </div>
                  ))}
                </div>
              )}
              
              <div style={{ borderTop: '1px solid #eee', padding: '6px 12px', display: 'flex', gap: 4, marginTop: 4 }}>
                <input
                  type="text"
                  value={customSubtaskInput}
                  onChange={e => setCustomSubtaskInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustomItem(customSubtaskInput); } }}
                  placeholder="Pridať vlastnú..."
                  style={{ flex: 1, fontSize: 12, padding: '3px 6px', border: '1px solid #ccc', borderRadius: 4 }}
                />
                <button type="button" onClick={() => addCustomItem(customSubtaskInput)} style={{ background: '#16a34a', color: 'white', border: 'none', borderRadius: 4, padding: '2px 10px', cursor: 'pointer', fontSize: 14 }}>+</button>
              </div>
            </div>
          )}
        </div>

        {/* Príloha */}
        <label style={{ 
          cursor: 'pointer', 
          padding: '0 12px', 
          background: '#eceff1', 
          color: '#455a64', 
          borderRadius: '6px', 
          fontSize: '13px', 
          height: '36px', 
          display: 'flex', 
          alignItems: 'center',
          border: '1px solid #cfd8dc',
          whiteSpace: 'nowrap'
        }}>
          📎 {formData.formFiles.length > 0 ? `${formData.formFiles.length} súb.` : 'Súbory'}
          <input
            type="file"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => {
              const files = Array.from(e.target.files);
              setFormData(prev => ({ ...prev, formFiles: [...prev.formFiles, ...files] }));
              e.target.value = '';
            }}
          />
        </label>

        {/* Tlačidlo PRIDAŤ */}
        <button type="submit" disabled={isSubmitting} style={{ 
          padding: "0 20px", 
          borderRadius: 6, 
          fontSize: 14, 
          fontWeight: 'bold',
          height: '36px', 
          background: isSubmitting ? '#78909c' : '#2e7d32', 
          color: 'white', 
          border: 'none', 
          cursor: isSubmitting ? 'wait' : 'pointer',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          {isSubmitting ? 'UKLADÁM...' : 'PRIDAŤ'}
        </button>
      </div>
    </form>
  );
}
